import React, { useState, useRef } from 'react'
import './index.css'
import store from '../../store'
import { useGlobal } from '../../context/global'
import { toast } from 'react-toastify'

const MaintenancePage = () => {
  const { api, maintenance } = useGlobal()
  const [showAdminLogin, setShowAdminLogin] = useState(false)
  const [loginStep, setLoginStep] = useState('INIT') // INIT | VERIFYING | OTP_SENT | VERIFYING_OTP
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [otpNotice, setOtpNotice] = useState('')

  const phoneInputRef = useRef()
  const otpInputRef = useRef()
  const contactsRef = useRef({ whatsapp: null, emails: [] })

  const handleSendAdminOTP = async (e) => {
    if (e) e.preventDefault()
    setErrorMessage('')
    const inputVal = (phone || '').trim().toPhoneCase()

    if (!inputVal || inputVal.length < 10) {
      setErrorMessage('Please enter a valid 10-digit registered phone number')
      return
    }

    setLoginStep('VERIFYING')
    try {
      const verifyRes = await api.call('/verify-user', { id: inputVal })
      const users = verifyRes.data || []
      const hasAppAdmin = users.some(u => u.roleID === 'app-admin')

      if (!hasAppAdmin) {
        setErrorMessage('Access Denied: Only App Administrators can sign in during maintenance.')
        setLoginStep('INIT')
        return
      }

      const emails = users.map(u => u.email).filter(Boolean)
      contactsRef.current = {
        whatsapp: users.find(u => u.whatsapp)?.whatsapp || inputVal,
        emails: emails
      }

      setLoginStep('SENDING_OTP')
      const otpRes = await api.call('/send-otp', {
        id: `sevabase-login-${inputVal}`,
        target: inputVal,
        emails: emails
      })

      const delivery = otpRes.data?.delivery || {}
      const channels = []
      if (delivery.whatsapp) channels.push('WhatsApp')
      if (delivery.mail) channels.push('Email')

      setOtpNotice(
        channels.length > 0
          ? `6-digit OTP sent to your registered ${channels.join(' & ')}.`
          : '6-digit OTP has been dispatched to your registered contact.'
      )
      setLoginStep('OTP_SENT')
    } catch (err) {
      console.error('Admin verification error:', err)
      const status = err.response?.status
      if (status === 503) {
        setErrorMessage('Access Denied: Website is under maintenance. Only App Administrators can sign in.')
      } else if (status === 404) {
        setErrorMessage('Phone number not found in administrator records.')
      } else {
        setErrorMessage(err.response?.data || err.message || 'Failed to verify administrator account.')
      }
      setLoginStep('INIT')
    }
  }

  const handleVerifyAdminOTP = async (e) => {
    if (e) e.preventDefault()
    setErrorMessage('')
    const cleanOtp = (otp || '').trim()

    if (!cleanOtp || cleanOtp.length !== 6) {
      setErrorMessage('Please enter the complete 6-digit OTP code')
      return
    }

    const inputVal = (phone || '').trim().toPhoneCase()
    setLoginStep('VERIFYING_OTP')

    try {
      const res = await api.call('/verify-otp', {
        id: `sevabase-login-${inputVal}`,
        otp: cleanOtp
      })

      const { access_token, users } = res.data
      sessionStorage.setItem('access_token', access_token)
      await store.set({
        index: 0,
        users: users
      })

      toast.success('Welcome back, App Administrator!')
      // Reload to activate app-admin view across all contexts
      window.location.reload()
    } catch (err) {
      console.error('Admin OTP verification error:', err)
      const status = err.response?.status
      if (status === 403) {
        setErrorMessage('Incorrect OTP code. Please check and try again.')
      } else if (status === 503) {
        setErrorMessage('Website is under maintenance. Only App Administrators are permitted.')
      } else {
        setErrorMessage(err.response?.data || 'Failed to verify OTP code. Please try again.')
      }
      setLoginStep('OTP_SENT')
    }
  }

  const resetAdminForm = () => {
    setShowAdminLogin(false)
    setLoginStep('INIT')
    setPhone('')
    setOtp('')
    setErrorMessage('')
    setOtpNotice('')
  }

  return (
    <div className="maintenance-container">
      {/* Top Bar with Brand and Status Indicator */}
      <div className="maintenance-topbar">
        <div className="maintenance-brand">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <span>SevaBase</span>
        </div>
        <div className="maintenance-status-pill">
          <div className="pulse-dot"></div>
          <span>Maintenance Mode</span>
        </div>
      </div>

      {/* Main Glassmorphism Maintenance Card */}
      <div className="maintenance-card">
        <div className="maintenance-hero-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="maintenance-title">Website Under Maintenance</h1>
        <div className="maintenance-greeting">Hare Krishna!</div>

        <div className="maintenance-message-box">
          {maintenance?.message || 'SevaBase is currently undergoing scheduled maintenance and updates to improve system stability. All volunteer services, registrations, and updates are temporarily paused.'}
        </div>

        {/* Informational Cards */}
        <div className="maintenance-features-grid">
          <div className="maintenance-feature-item">
            <div className="maintenance-feature-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="maintenance-feature-text">
              All existing data, records, and seva assignments are safely stored and secure.
            </div>
          </div>

          <div className="maintenance-feature-item">
            <div className="maintenance-feature-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                <polyline points="12 6 12 12 16 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="maintenance-feature-text">
              Services will resume shortly once maintenance concludes. Thank you for your patience.
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="maintenance-actions">
          <a
            href="https://wa.me/916364903725"
            target="_blank"
            rel="noopener noreferrer"
            className="maintenance-contact-btn"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Contact Support on WhatsApp</span>
          </a>

          {!showAdminLogin ? (
            <button
              type="button"
              className="maintenance-admin-toggle-btn"
              onClick={() => setShowAdminLogin(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>App Admin Sign In</span>
            </button>
          ) : (
            <div className="maintenance-admin-form">
              <div className="admin-form-header">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21 2l-2 2m-6 6l-3-3m0 0l-3 3 6 6 3-3-3-3zM3 21l6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>App Administrator Access</span>
              </div>
              <div className="admin-form-hint">
                Only the designated App Administrator can sign in while maintenance is active.
              </div>

              {errorMessage && (
                <div className="admin-error-box">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              {loginStep !== 'OTP_SENT' && loginStep !== 'VERIFYING_OTP' ? (
                <form onSubmit={handleSendAdminOTP} className="admin-input-group">
                  <input
                    ref={phoneInputRef}
                    type="tel"
                    className="admin-input"
                    placeholder="Registered phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={loginStep === 'VERIFYING' || loginStep === 'SENDING_OTP'}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="admin-submit-btn"
                    disabled={loginStep === 'VERIFYING' || loginStep === 'SENDING_OTP'}
                  >
                    {loginStep === 'VERIFYING' ? 'Verifying Admin...' : loginStep === 'SENDING_OTP' ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyAdminOTP} className="admin-input-group">
                  {otpNotice && <div className="admin-form-hint">{otpNotice}</div>}
                  <input
                    ref={otpInputRef}
                    type="number"
                    maxLength={6}
                    className="admin-input"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    disabled={loginStep === 'VERIFYING_OTP'}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="admin-submit-btn"
                    disabled={loginStep === 'VERIFYING_OTP'}
                  >
                    {loginStep === 'VERIFYING_OTP' ? 'Verifying...' : 'Verify & Enter'}
                  </button>
                </form>
              )}

              <button
                type="button"
                className="admin-cancel-btn"
                onClick={resetAdminForm}
              >
                Cancel Admin Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MaintenancePage
